import { attachUpload } from "@/api";
import { uploadFile } from "@/utils/uploadFile";
import { validateImageFile } from "@/utils/validateImageFile";

import type { UploadScope } from "@/components/tiptap/utils/uploadScope";

type ImageAttrs = { key: string; name?: string } | { src: string; name?: string };

/**
 * Uploads an image file and returns the attrs for an image node.
 *
 * SVG files are sanitized and embedded as base64 data URIs.
 * Other image formats (JPEG, PNG, GIF, WebP) are uploaded directly to storage
 * and then attached to the scope entity (document or chat).
 *
 * @param file - The image file to upload
 * @param scope - The document or chat to attach the image to
 * @param options - Upload options, e.g. an `AbortSignal` to cancel the upload
 * @returns Image attributes (key/src and name) or null if validation fails
 */
export async function uploadImage(
  file: File,
  scope: UploadScope,
  options: { signal?: AbortSignal } = {},
): Promise<null | ImageAttrs> {
  const fileType = validateImageFile(file);
  if (!fileType) {
    return null;
  }

  if (fileType === "image/svg+xml") {
    const text = await file.text();
    const { sanitizeSvg } = await import(
      /* webpackChunkName: "sanitizeSvg" */ "@/utils/sanitizeSvg"
    );
    const sanitizedSvg = sanitizeSvg(text);

    const src = `data:image/svg+xml,${encodeURIComponent(sanitizedSvg)}`;

    return { src, name: file.name };
  }

  const key = await uploadFile(file, options);
  await attachUpload(scope.type, scope.uid, key, undefined, { signal: options.signal });

  return { key, name: file.name };
}
